"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { DetailedListing, DetailedListingViewAs } from "@/lib/types";
import { useTranslations } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

const CHOICES: NonNullable<DetailedListingViewAs>[] = ["OWNER", "PUBLIC_BUYER"];

export type ListingViewAsProps = {
  listing: DetailedListing;
};

export function ListingViewAs({ listing }: ListingViewAsProps) {
  const { user } = useCurrentUser();
  const t = useTranslations();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [viewAs, setViewAs] =
    useState<NonNullable<DetailedListingViewAs>>("OWNER");

  const [requestInProgress, setRequestInProgress] = useState(false);

  const isOwner =
    user && (user.id === listing.ownerId || user.id === listing.sellerAgentId);

  function handleSelectValueChange(value: string) {
    if (CHOICES.includes(value as NonNullable<DetailedListingViewAs>)) {
      setViewAs(value as NonNullable<DetailedListingViewAs>);
    }
  }

  useEffect(() => {
    const url = new URL(`${pathname}?${searchParams}`, window.location.origin);
    const currentViewAs =
      (url.searchParams.get("viewAs") as DetailedListingViewAs | null) ??
      "OWNER";

    if (currentViewAs === viewAs) {
      setRequestInProgress(false);
      return;
    }

    setRequestInProgress(true);

    if (viewAs !== "OWNER") {
      url.searchParams.set("viewAs", viewAs);
    } else {
      url.searchParams.delete("viewAs");
    }

    router.replace(url.toString());
  }, [viewAs, pathname, router, searchParams]);

  useEffect(() => {
    const viewAs = searchParams.get("viewAs") as DetailedListingViewAs | null;

    if (!viewAs) {
      setViewAs("OWNER");
    } else if (CHOICES.includes(viewAs)) {
      setViewAs(viewAs);
    }

    setRequestInProgress(false);
  }, [searchParams]);

  if (!isOwner) {
    return null;
  }

  return (
    <div className="flex gap-2 items-center">
      <div className="whitespace-nowrap">
        {t("saas.listing.header.actions.viewAs.label")}
      </div>

      <Select
        onValueChange={handleSelectValueChange}
        value={viewAs}
        disabled={requestInProgress}
      >
        <SelectTrigger>
          {t(`saas.listing.header.actions.viewAs.${viewAs}`)}
        </SelectTrigger>

        <SelectContent>
          {CHOICES.map((choice) => (
            <SelectItem key={choice} value={choice}>
              {t(`saas.listing.header.actions.viewAs.${choice}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

ListingViewAs.displayName = "ListingViewAs";
